// ---------------------------------------------------------------------------
// 3.10: Internal Ops Monitoring Dashboard (Platform Admin)
// ---------------------------------------------------------------------------
// Real-time operational health: Workers, queues, event pipeline, errors,
// top tenants, and recent alerts. Auto-refreshes every 30 seconds.
// ---------------------------------------------------------------------------

import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WorkerHealth {
  name: string;
  status: "healthy" | "degraded" | "down" | "unknown";
  lastCheckAt: string | null;
  requestCount1h: number;
  errorCount1h: number;
  p95LatencyMs: number;
}

interface HealthSummary {
  totalWorkers: number;
  healthyCount: number;
  errorRate1h: number;
  p95LatencyMs: number;
}

interface QueueStatus {
  name: string;
  depth: number;
  processingRate: number;
  oldestMessageAt: string | null;
  dlqCount: number;
}

interface QueueSummary {
  totalDepth: number;
  totalDlq: number;
}

interface PipelineStats {
  eventsPerMin: number;
  webhooksPerMin: number;
  materializerPerMin: number;
  failedEventsLastHour: number;
  reconcilerLag: number;
  producerWorkers: number;
}

interface TopTenant {
  tenantId: string;
  name: string;
  slug: string;
  eventCount24h: number;
  distinctEvents: number;
  lastActivity: string;
}

interface ErrorHour {
  hour: string;
  errorCount: number;
  affectedWorkers: number;
}

interface Alert {
  id: string;
  severity: "critical" | "high" | "warning" | "info";
  message: string;
  source: string;
  firedAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REFRESH_INTERVAL_MS = 30_000;

const STATUS_COLORS: Record<string, string> = {
  healthy: "border-[var(--color-success)]/50 bg-[var(--color-success)]/5",
  degraded: "border-yellow-500/50 bg-[var(--color-warning)]/5",
  down: "border-[var(--color-destructive)]/50 bg-[var(--color-destructive)]/5",
  unknown: "border-border bg-muted/30",
};

const STATUS_DOT: Record<string, string> = {
  healthy: "bg-[var(--color-success)]/50",
  degraded: "bg-[var(--color-warning)]/50",
  down: "bg-[var(--color-destructive)]/50",
  unknown: "bg-muted-foreground/50",
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: "bg-[var(--color-destructive)] text-white",
  high: "bg-[var(--color-destructive)]/20 text-[var(--color-destructive)]/80",
  warning: "bg-[var(--color-warning)]/20 text-yellow-400",
  info: "bg-[var(--color-primary)]/20 text-[var(--color-primary)]/70",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} className="h-20" />
        ))}
      </div>
      <Skeleton className="h-64 w-full rounded-lg" />
      <Skeleton className="h-48 w-full rounded-lg" />
    </div>
  );
}

function SummaryCard({ label, value, detail, alert }: {
  label: string;
  value: string | number;
  detail?: string;
  alert?: boolean;
}) {
  return (
    <div className={`stagger-item rounded-lg border p-4 ${alert ? "border-[var(--color-destructive)]/50 bg-[var(--color-destructive)]/5" : "border-border"}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${alert ? "text-[var(--color-destructive)]/80" : "text-foreground"}`}>{value}</p>
      {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
    </div>
  );
}

function WorkerGrid({ workers }: { workers: WorkerHealth[] }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {workers.map((w) => (
        <div
          key={w.name}
          className={`stagger-item rounded-lg border p-3 ${STATUS_COLORS[w.status]}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${STATUS_DOT[w.status]}`} />
              <span className="text-sm font-mono font-medium text-foreground">{w.name}</span>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {w.status}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
            <span>Req: {formatNumber(w.requestCount1h)}</span>
            <span className={w.errorCount1h > 0 ? "text-[var(--color-destructive)]/80" : ""}>
              Err: {w.errorCount1h}
            </span>
            <span>P95: {w.p95LatencyMs}ms</span>
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground/70">
            Checked: {formatRelativeTime(w.lastCheckAt)}
          </p>
        </div>
      ))}
    </div>
  );
}

function QueueTable({ queues }: { queues: QueueStatus[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Queue</th>
            <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Depth</th>
            <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Rate/min</th>
            <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Oldest</th>
            <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">DLQ</th>
          </tr>
        </thead>
        <tbody>
          {queues.map((q) => (
            <tr
              key={q.name}
              className={`stagger-item border-b border-border ${q.dlqCount > 0 ? "bg-[var(--color-destructive)]/5" : ""}`}
            >
              <td className="px-4 py-2 font-mono text-foreground">{q.name}</td>
              <td className="px-4 py-2 text-right text-foreground">{q.depth}</td>
              <td className="px-4 py-2 text-right text-muted-foreground">{q.processingRate}</td>
              <td className="px-4 py-2 text-right text-muted-foreground">
                {formatRelativeTime(q.oldestMessageAt)}
              </td>
              <td className="px-4 py-2 text-right">
                <span className={q.dlqCount > 0 ? "font-medium text-[var(--color-destructive)]/80" : "text-muted-foreground"}>
                  {q.dlqCount}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventPipeline({ stats }: { stats: PipelineStats }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="font-mono text-xs text-muted-foreground leading-relaxed">
        <div className="flex items-center gap-2">
          <span className="inline-block w-48 text-right text-foreground">
            Producers ({stats.producerWorkers} Workers)
          </span>
          <span className="text-muted-foreground">---&gt;</span>
          <span className="text-foreground font-medium">Event Router</span>
          <span className="text-muted-foreground">---&gt;</span>
          <span className="text-foreground">ClickHouse</span>
          <span className="text-[var(--color-success)]">({stats.eventsPerMin.toFixed(1)} events/min)</span>
        </div>
        <div className="flex items-center gap-2 ml-[212px]">
          <span className="text-muted-foreground/50">{"                    "}|---&gt;</span>
          <span className="text-foreground">Materializers</span>
          <span className="text-[var(--color-primary)]/70">({stats.materializerPerMin.toFixed(1)}/min)</span>
        </div>
        <div className="flex items-center gap-2 ml-[212px]">
          <span className="text-muted-foreground/50">{"                    "}|---&gt;</span>
          <span className="text-foreground">Webhooks</span>
          <span className="text-[var(--color-secondary)]/70">({stats.webhooksPerMin.toFixed(1)}/min)</span>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div className="rounded border border-border p-2 text-center">
          <p className="text-xs text-muted-foreground">Events/min</p>
          <p className="text-lg font-bold text-foreground">{stats.eventsPerMin.toFixed(1)}</p>
        </div>
        <div className={`rounded border p-2 text-center ${stats.failedEventsLastHour > 0 ? "border-[var(--color-destructive)]/50" : "border-border"}`}>
          <p className="text-xs text-muted-foreground">Failed (1h)</p>
          <p className={`text-lg font-bold ${stats.failedEventsLastHour > 0 ? "text-[var(--color-destructive)]/80" : "text-foreground"}`}>
            {stats.failedEventsLastHour}
          </p>
        </div>
        <div className={`rounded border p-2 text-center ${stats.reconcilerLag > 100 ? "border-yellow-500/50" : "border-border"}`}>
          <p className="text-xs text-muted-foreground">Reconciler Lag</p>
          <p className="text-lg font-bold text-foreground">{stats.reconcilerLag}</p>
        </div>
      </div>
    </div>
  );
}

function TenantTable({ tenants }: { tenants: TopTenant[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Tenant</th>
            <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Events (24h)</th>
            <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Event Types</th>
            <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Last Active</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((t) => (
            <tr key={t.tenantId} className="stagger-item border-b border-border">
              <td className="px-4 py-2">
                <span className="text-foreground font-medium">{t.name}</span>
                <span className="ml-2 text-xs text-muted-foreground font-mono">{t.slug}</span>
              </td>
              <td className="px-4 py-2 text-right text-foreground font-mono">
                {formatNumber(t.eventCount24h)}
              </td>
              <td className="px-4 py-2 text-right text-muted-foreground">{t.distinctEvents}</td>
              <td className="px-4 py-2 text-right text-muted-foreground">
                {formatRelativeTime(t.lastActivity)}
              </td>
            </tr>
          ))}
          {tenants.length === 0 && (
            <tr>
              <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                No tenant activity in last 24 hours
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ErrorSparkline({ hours }: { hours: ErrorHour[] }) {
  const maxErrors = Math.max(...hours.map((h) => h.errorCount), 1);

  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex items-end gap-[2px] h-24">
        {hours.map((h, i) => {
          const height = maxErrors > 0 ? (h.errorCount / maxErrors) * 100 : 0;
          const isRecent = i >= hours.length - 3;
          return (
            <div
              key={h.hour}
              className="group relative flex-1"
              title={`${h.hour}: ${h.errorCount} errors`}
            >
              <div
                className={`w-full rounded-t transition-all ${
                  h.errorCount === 0
                    ? "bg-muted/30"
                    : isRecent
                      ? "bg-[var(--color-destructive)]/80"
                      : "bg-[var(--color-destructive)]/40"
                }`}
                style={{ height: `${Math.max(height, 2)}%` }}
              />
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block rounded bg-foreground px-2 py-1 text-[10px] text-background whitespace-nowrap z-10">
                {h.errorCount} errors at {new Date(h.hour).getHours()}:00
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
        <span>24h ago</span>
        <span>12h ago</span>
        <span>Now</span>
      </div>
    </div>
  );
}

function AlertList({ alerts }: { alerts: Alert[] }) {
  return (
    <div className="space-y-2">
      {alerts.map((a) => (
        <div
          key={a.id}
          className={`stagger-item flex items-start gap-3 rounded-lg border p-3 ${
            a.acknowledgedAt ? "border-border opacity-60" : "border-border"
          }`}
        >
          <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${SEVERITY_COLORS[a.severity]}`}>
            {a.severity}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground">{a.message}</p>
            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
              <span>{a.source}</span>
              <span>{formatRelativeTime(a.firedAt)}</span>
              {a.acknowledgedAt && (
                <span className="text-[var(--color-success)]">
                  Ack by {a.acknowledgedBy}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
      {alerts.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">No recent alerts</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AdminOperationsPage() {
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState<WorkerHealth[]>([]);
  const [healthSummary, setHealthSummary] = useState<HealthSummary | null>(null);
  const [queues, setQueues] = useState<QueueStatus[]>([]);
  const [queueSummary, setQueueSummary] = useState<QueueSummary | null>(null);
  const [pipeline, setPipeline] = useState<PipelineStats | null>(null);
  const [topTenants, setTopTenants] = useState<TopTenant[]>([]);
  const [errorHours, setErrorHours] = useState<ErrorHour[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAll = useCallback(async () => {
    const results = await Promise.allSettled([
      apiFetch<{ summary: HealthSummary; workers: WorkerHealth[] }>(
        "/api/admin/operations/health",
      ).then((r) => {
        setHealthSummary(r.summary);
        setWorkers(r.workers);
      }),
      apiFetch<{ summary: QueueSummary; queues: QueueStatus[] }>(
        "/api/admin/operations/queues",
      ).then((r) => {
        setQueueSummary(r.summary);
        setQueues(r.queues);
      }),
      apiFetch<PipelineStats>("/api/admin/operations/pipeline").then(setPipeline),
      apiFetch<{ tenants: TopTenant[] }>("/api/admin/operations/tenants/top").then(
        (r) => setTopTenants(r.tenants),
      ),
      apiFetch<{ hours: ErrorHour[] }>("/api/admin/operations/errors").then(
        (r) => setErrorHours(r.hours),
      ),
      apiFetch<{ alerts: Alert[] }>("/api/admin/operations/alerts").then(
        (r) => setAlerts(r.alerts),
      ),
    ]);

    const failCount = results.filter((r) => r.status === "rejected").length;
    if (failCount > 0) {
      // Partial data is acceptable — show what we have
    }

    setLastRefresh(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
    intervalRef.current = setInterval(fetchAll, REFRESH_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-foreground">Operations</h1>
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Operations</h1>
          <p className="text-sm text-muted-foreground">
            Auto-refreshes every 30s — Last: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <button
          onClick={fetchAll}
          className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          Refresh now
        </button>
      </div>

      {/* Section 1: Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <SummaryCard
          label="Total Workers"
          value={healthSummary?.totalWorkers ?? 27}
        />
        <SummaryCard
          label="Healthy"
          value={healthSummary?.healthyCount ?? 0}
          detail={`of ${healthSummary?.totalWorkers ?? 27}`}
          alert={healthSummary !== null && healthSummary.healthyCount < healthSummary.totalWorkers}
        />
        <SummaryCard
          label="Queue Depth"
          value={formatNumber(queueSummary?.totalDepth ?? 0)}
        />
        <SummaryCard
          label="DLQ Count"
          value={queueSummary?.totalDlq ?? 0}
          alert={(queueSummary?.totalDlq ?? 0) > 0}
        />
        <SummaryCard
          label="Error Rate (1h)"
          value={healthSummary?.errorRate1h ?? 0}
          alert={(healthSummary?.errorRate1h ?? 0) > 0}
        />
        <SummaryCard
          label="P95 Latency"
          value={`${healthSummary?.p95LatencyMs ?? 0}ms`}
          alert={(healthSummary?.p95LatencyMs ?? 0) > 1000}
        />
      </div>

      {/* Section 2: Queue Depths */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-foreground">Queue Depths</h2>
        <QueueTable queues={queues} />
      </section>

      {/* Section 3: Worker Health Grid */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-foreground">Worker Health</h2>
        <WorkerGrid workers={workers} />
      </section>

      {/* Section 4: Event Pipeline */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-foreground">Event Pipeline</h2>
        {pipeline ? <EventPipeline stats={pipeline} /> : (
          <p className="text-sm text-muted-foreground">Pipeline data unavailable</p>
        )}
      </section>

      {/* Section 5: Top Tenants */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-foreground">Top 10 Tenants (24h)</h2>
        <TenantTable tenants={topTenants} />
      </section>

      {/* Section 6: Error Rate Trends */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-foreground">Error Trends (24h)</h2>
        {errorHours.length > 0 ? (
          <ErrorSparkline hours={errorHours} />
        ) : (
          <p className="text-sm text-muted-foreground">No error data available</p>
        )}
      </section>

      {/* Section 7: Recent Alerts */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-foreground">Recent Alerts</h2>
        <AlertList alerts={alerts} />
      </section>
    </div>
  );
}
